/**
 * Whether this document is the hidden frame a console is waiting on — asked before anything boots.
 *
 * **Why a frame is involved at all.** A page has one way to ask Entra for a session without leaving:
 * a hidden `<iframe>` sent there with `prompt=none`, which Entra answers by redirecting that frame
 * to the app's redirect URI with a code, or with an error, in the fragment. The console does it in
 * two places. A new tab has no tokens of its own, and asks whether the person last seen here is
 * still signed in (`ssoSilent`, `auth/keptSignIn.ts`). And Entra gives a browser app a refresh
 * token with a fixed life of 24 hours; past it the frame is `acquireTokenSilent`'s last resort.
 *
 * **Why the console has to notice it is that frame.** Since MSAL 5 the page that made the frame no
 * longer reads the frame's address. It listens on a `BroadcastChannel` and waits for the document
 * *inside* the frame to hand the response over (`waitForBridgeResponse`, ten seconds by default) —
 * the "redirect bridge", `broadcastResponseToMainFrame()` of `@azure/msal-browser/redirect-bridge`.
 * The console's redirect URI is the bare origin (`computeRedirectUri`, `auth/msal.ts`), so what
 * loads in the frame is `index.html`. Left to itself that is a second console booting where nobody
 * can see it: it broadcasts nothing, and the page that asked gets `timed_out` ten seconds later.
 *
 * **Why here and not on a page of its own.** MSAL's documentation gives the bridge a blank page at
 * its own redirect URI. Entra matches a redirect URI by exact string, the registration holds the
 * bare origin, and adding one is a step done by hand in every tenant's registration
 * (`docs/azure-onboarding.md` §3b). So the bridge runs at the URI that is already registered:
 * `entry.ts` asks this question first and loads either the bridge or the console, never both.
 *
 * **The top-level leg is not this.** Signing in by redirect comes back to the same URI with the
 * same kind of fragment, but in the tab itself, and that response is `handleRedirectPromise`'s to
 * read. `framed` is what tells the two apart, and nothing here touches the address.
 */

/** Where the document finds itself. `entry.ts` reads it off `window`; a test writes it by hand. */
export interface DocumentPlace {
  /** `window.parent !== window` — true in any frame, whoever made it. */
  framed: boolean;
  /** `location.hash`. MSAL asks Entra for the fragment (`responseMode`, left at its default). */
  hash: string;
  /** `location.search`, read as well because the bridge itself accepts a response in either. */
  search: string;
}

/**
 * An authorization response, by the two shapes OAuth gives it: `state` with a `code`, or `state`
 * with an `error` (RFC 6749 §4.1.2). Entra's refusal of a silent request — nobody signed in there,
 * consent owed — is the second shape, and it has to reach the waiting page as surely as a code
 * does: it is what tells MSAL that a person is needed, where silence would only tell it ten
 * seconds had passed.
 *
 * **`state` alone is not enough, and `code` alone is not either.** Both are words the console uses
 * itself: `/link?code=…` is the address a headset prints, and the design preview takes
 * `?preview=chat&state=…`. Neither carries the other.
 */
export function carriesAuthResponse(part: string): boolean {
  const params = new URLSearchParams(part.replace(/^[#?]/, ""));
  return params.has("state") && (params.has("code") || params.has("error"));
}

export function isRedirectBridgeDocument(place: DocumentPlace): boolean {
  return place.framed && (carriesAuthResponse(place.hash) || carriesAuthResponse(place.search));
}
